import deployment from "../../deployment.json";
import type { TAuthConfig, TRefreshTokenExpiredEvent } from "react-oauth2-code-pkce";

/** Production builds share the CDK defaults. Development without explicit
 * OIDC values retains the local proxy workflow. VITE_* overrides are compiled
 * into the bundle, so the deployment build checks them against backend overrides. */
const issuer = import.meta.env.VITE_OIDC_ISSUER ?? (import.meta.env.DEV ? "" : deployment.oidcIssuer);
const clientId = import.meta.env.VITE_OIDC_CLIENT_ID ?? (import.meta.env.DEV ? "" : deployment.oidcClientId);

export const authEnabled = Boolean(issuer && clientId);

export const authConfig: TAuthConfig = {
  clientId,
  // Keep tokens and PKCE state separate across issuers and clients.
  storageKeyPrefix: `CRE_${encodeURIComponent(issuer)}_${clientId}_`,
  // Keycloak's standard OIDC endpoint shape — identical for every realm.
  authorizationEndpoint: `${issuer}/protocol/openid-connect/auth`,
  tokenEndpoint: `${issuer}/protocol/openid-connect/token`,
  logoutEndpoint: `${issuer}/protocol/openid-connect/logout`,
  redirectUri: `${window.location.origin}/`,
  logoutRedirect: `${window.location.origin}/`,
  scope: "openid email profile",
  // false, so the app paints its own sign-in screen first. CPL had this true
  // and the redirect fired before React could render anything — "the login
  // page never opens, I go directly to SSO".
  autoLogin: false,
  decodeToken: true,
  onRefreshTokenExpire: (event: TRefreshTokenExpiredEvent) =>
    event.logIn(undefined, undefined, "redirect"),
};

/**
 * Empty in dev, so requests go to /api on the Vite dev server and through its
 * proxy, which attaches the shared secret server-side. In a built bundle it is
 * the real API origin and the browser sends a JWT instead.
 */
export const API_BASE =
  import.meta.env.VITE_API_BASE ??
  (import.meta.env.DEV ? "" : deployment.apiBase);

/**
 * Dev-only: the proxy supplies identity, so the app must render its UI rather
 * than its sign-in screen even though no IdP is configured. Never true in a
 * production build — `import.meta.env.DEV` is compiled to false by Vite.
 */
export const devBypass = import.meta.env.DEV && !authEnabled;

/**
 * The host the sign-in redirect actually lands on, derived from the issuer.
 *
 * Shown on the sign-in screen so someone can check where their password is
 * going. It was hardcoded to idp.myneva.cloud and the staging client turned
 * out to live on idp-staging.myneva.cloud — a wrong hostname on an
 * anti-phishing line is worse than no line at all, so it reads the real value.
 */
export const idpHost = (() => {
  try {
    return new URL(issuer).host;
  } catch {
    return "";
  }
})();
